import React, { useEffect, useState, useCallback } from "react"
import axios from "axios"
import Link from "@cloudscape-design/components/link"

import { useAuthContext } from "../../auth/context"
import { hasLibrarianAccess, getUsernameFromToken } from "../../utils/auth"

import PageLayout from "../app/page-layout"
import EnhancedTable from "../home/enhanced-table"
import LoadingSpinner from "../common/loading-spinner"
import ErrorMessage from "../common/error-message"
import ErrorBoundary from "../common/error-boundary"

import Documents from "../../api/documents"
import ImportDocuments from "../../api/import-documents"

const Home = () => {
  const { token, logout, settings } = useAuthContext()
  const [ table, setTable ] = useState([])
  const [ apiEndpoint, setApiEndpoint ] = useState(null)
  const [ isLoading, setIsLoading ] = useState(true)
  const [ error, setError ] = useState(null)
  
  // Check if user has librarian access
  const isLibrarian = hasLibrarianAccess(token)
  const username = getUsernameFromToken(token)

  // Handle logout - use the context's logout function
  const handleLogout = useCallback(() => {
    logout()
  }, [logout])

  // Fetch data from API
  const fetchData = useCallback(() => {
    if (!apiEndpoint) return

    setIsLoading(true)
    setError(null)

    Documents(token, apiEndpoint).then(list => {
      console.log(list)
      setTable(list)
      setIsLoading(false)
    }).catch(err => {
      console.error('Failed to fetch documents:', err)
      
      // Handle authentication errors with redirect
      if (err.message.includes('Authentication failed')) {
        setError('Your session has expired. Redirecting to login...')
        setTimeout(() => {
          window.location.href = '/login'
        }, 2000)
      } else {
        setError(err.message || 'Failed to load data. Please try again.')
      }
      
      setIsLoading(false)
    })
  }, [token, apiEndpoint])

  useEffect(() => {
    axios.get("/settings.json").then(resp => {
      setApiEndpoint(resp.data.apiEndpoint)
    }).catch(console.log) 
  }, [])

  useEffect(() => {
    if (apiEndpoint) {
      fetchData()
    }
  }, [apiEndpoint, fetchData])

  // Handle CSV import
  const handleImportComplete = async (records) => {
    if (!apiEndpoint) {
      throw new Error('API endpoint not configured')
    }

    try {
      await ImportDocuments(token, apiEndpoint, records)
      // Refresh table data after successful import
      await fetchData()
    } catch (error) {
      // Re-throw error to be handled by the table component
      throw error
    }
  }

  return (
    <PageLayout 
      title="Document Identifier Registry"
      description={
        <Link
          external
          href="https://amazongbr.sharepoint.com/sites/FSIDocRepo"
          variant="primary"
        >
          [GO TO - WW FSI Document Repository]
        </Link>
      }
      username={username}
      userRole={isLibrarian ? 'Librarian' : null}
      onLogout={handleLogout}
    >
      <ErrorBoundary 
        fallbackMessage="The data table encountered an error. Please try again."
        onReset={fetchData}
      >
        {isLoading ? (
          <LoadingSpinner message="Loading data..." />
        ) : error ? (
          <ErrorMessage 
            message={error}
            onRetry={error.includes('Redirecting') ? null : fetchData}
            showRetry={!error.includes('Redirecting')}
          />
        ) : (
          <EnhancedTable 
            data={table} 
            token={token}
            isLibrarian={isLibrarian}
            username={username}
            onImportComplete={handleImportComplete}
            apiEndpoint={apiEndpoint}
            onDataChange={fetchData}
            settings={settings}
          />
        )}
      </ErrorBoundary>
    </PageLayout>
  )
}

export default Home